import { useBehavior, useButton, useDetectDocumentScroll } from '@libs/hooks';
import { AssetsSvgMap, TAssetsSvgMapValue, TButtonData, TMargin, TWidget } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, MouseEvent } from 'react';
import { SvgComponent } from '../../common/Svg/SvgComponent';
import { TButtonOption } from '../../types/options';
import { capitalText, getInlineStyles } from '../../utils/styles';
import styles from './styles/buttonCircle.module.css';

type TButtonCircle<TD, TO> =
    | ({
          isWidget: false;
          iconName?: TAssetsSvgMapValue;
          iconColor?: string;
          position?: 'floating' | 'inline';
          size?: 'small' | 'medium';
          handleClick?: (e: MouseEvent<HTMLElement>) => void;
          isEnabled?: boolean;
          iconWidth: number;
          iconHeight: number;
      } & TO)
    | {
          isWidget: true;
          widget: TWidget<TD, TO>;
      };

export function ButtonCircle(props: TButtonCircle<TButtonData, TButtonOption>) {
    const { isWidget } = props;
    const { handleClick } = useButton(isWidget ? props.widget : ({} as TWidget<TButtonData>));
    const { isDisabled, isHidden } = useBehavior(isWidget ? props.widget : ({} as TWidget<TButtonData>));
    const { documentScrolled } = useDetectDocumentScroll(isWidget ? props.widget.uid : '');
    const margin = isWidget ? props.widget.options.margin : ({} as TMargin);
    const iconName = isWidget ? props.widget?.options?.iconName : props.iconName;
    const hierarchy = (isWidget ? props.widget?.options?.hierarchy : props.hierarchy) ?? 'tertiary';
    const size = isWidget ? props.widget?.options?.size : props.size;
    const label = isWidget ? props.widget?.data?.label : '';
    const icon = AssetsSvgMap[iconName as keyof typeof AssetsSvgMap] ?? 'ic_x';
    const iconWidth = isWidget ? 24 : props.iconWidth;
    const iconHeight = isWidget ? 24 : props.iconHeight;

    const getPosition = () => {
        const dynamicShadow = isWidget ? props.widget?.options?.dynamicShadow || true : true;
        if (dynamicShadow && documentScrolled && !isDisabled) {
            return 'floating';
        } else if (isWidget) {
            return props.widget?.options?.position;
        } else {
            return props.position;
        }
    };
    const raised = getPosition() === 'floating' && !isDisabled;

    const getFill = (hierarchy: TButtonOption['hierarchy']): string => {
        switch (hierarchy) {
            case 'secondary':
                return isDisabled ? 'purple_40_tr' : 'purple_100';
            case 'tertiary':
                return isDisabled ? 'grey_40_lc' : 'black';
            case 'destructive':
                return isDisabled ? 'pink_40_lc' : 'pink_100';
            default:
                return isDisabled ? 'white_40_tr_base' : 'white_base';
        }
    };

    const classNames = clsx(
        styles.buttonCircle,
        styles[`background${capitalText(hierarchy)}`],
        size === 'small' && styles.small,
        raised && styles.boxShadow,
        isDisabled && styles.disabled,
    );
    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <button
                aria-label={label}
                aria-disabled={isDisabled}
                aria-hidden={isHidden}
                disabled={isDisabled}
                className={classNames}
                style={getInlineStyles({ margin })}
                onClick={(e: MouseEvent<HTMLElement>) => {
                    if (isWidget) {
                        handleClick();
                    } else {
                        props?.handleClick?.(e);
                    }
                }}
                title={label}
                data-test={isWidget ? props.widget?.uid : ''}
            >
                {icon && (
                    <SvgComponent
                        width={iconWidth}
                        height={iconHeight}
                        name={icon as TAssetsSvgMapValue}
                        defaultName={AssetsSvgMap.ic_x}
                        fill={getFill(hierarchy)}
                    />
                )}
            </button>
        </Activity>
    );
}
